/**
 * Inactivity Timeout Service
 *
 * Automatically logs users out after 45 minutes of inactivity.
 * Refreshes tokens every 4 minutes while user is active.
 * Keeps session alive during video playback with heartbeat.
 */

import { performLogout } from './logoutService';
import { getLoginUrl } from '../utils/subdomain';
import { clearTokens, getRefreshToken as getStoredRefresh, setAccessToken as setStoredAccessToken, setRefreshToken as setStoredRefreshToken } from '../utils/tokenStorage';

// TESTING: Reduced timeouts for quick testing (change back to production values after testing)
// const INACTIVITY_TIMEOUT = 2 * 60 * 1000; // TEST: 2 minutes (PRODUCTION: 45 * 60 * 1000)
// const REFRESH_INTERVAL = 30 * 1000; // TEST: 30 seconds (PRODUCTION: 4 * 60 * 1000)

// PRODUCTION VALUES (uncomment after testing):
const INACTIVITY_TIMEOUT = 45 * 60 * 1000; // 45 minutes in ms
const REFRESH_INTERVAL = 4 * 60 * 1000; // Refresh every 4 minutes

// Get API base URL from environment - use same pattern as existing API clients
const PUBLIC_API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;
const TOKEN_REFRESH_ENDPOINT = '/account/refresh-token';

// Activity events to track
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

interface RefreshTokenResponse {

  success: boolean;
  access_token: string;
  refresh_token: string;
  expires_in: number; // seconds (typically 300 = 5 min)
  refresh_expires_in: number; // seconds (typically 2700 = 45 min)
  token_type: string;
}

class InactivityService {
  private lastActivityTime: number;
  private refreshIntervalId: NodeJS.Timeout | null = null;
  private videoHeartbeatIntervalId: NodeJS.Timeout | null = null;
  private isRefreshing: boolean = false;
  private isVideoPlaying: boolean = false;
  private activityListeners: Array<{ event: string; handler: EventListener }> = [];

  constructor() {
    this.lastActivityTime = Date.now();
    console.log('[InactivityService] Configured with API URL:', PUBLIC_API_BASE_URL);
  }

  /**
   * Initialize the inactivity service
   * Sets up activity listeners and token refresh interval
   */
  public initialize(): void {
    if (this.refreshIntervalId) {
      console.warn('[InactivityService] Already initialized');
      return;
    }

    console.log('[InactivityService] Initializing with 45-minute inactivity timeout');

    // Set up activity listeners
    this.setupActivityListeners();

    // Start periodic check for inactivity and token refresh
    // First check will happen after REFRESH_INTERVAL (4 minutes)
    this.refreshIntervalId = setInterval(() => {
      this.checkActivityAndRefresh();
    }, REFRESH_INTERVAL);

    console.log('[InactivityService] Service initialized - first token refresh in 4 minutes');
  }

  /**
   * Set up event listeners to track user activity
   */
  private setupActivityListeners(): void {
    ACTIVITY_EVENTS.forEach((event) => {
      const handler = () => {
        this.updateLastActivity();
      };

      window.addEventListener(event, handler, { passive: true });
      this.activityListeners.push({ event, handler });
    });

    console.log('[InactivityService] Activity listeners registered');
  }

  /**
   * Update last activity timestamp
   */
  private updateLastActivity(): void {
    this.lastActivityTime = Date.now();
  }

  /**
   * Check if user is inactive and handle accordingly
   * Called every REFRESH_INTERVAL (4 minutes)
   */
  private async checkActivityAndRefresh(): Promise<void> {
    const idleTime = Date.now() - this.lastActivityTime;

    // If video is playing, always refresh (keep session alive)
    if (this.isVideoPlaying) {
      console.log('[InactivityService] Video playing - refreshing token');
      await this.refreshAccessToken();
      return;
    }

    // If user is inactive for 45+ minutes, logout
    if (idleTime >= INACTIVITY_TIMEOUT) {
      console.log('[InactivityService] User inactive for 45+ minutes - logging out');
      this.logout();
      return;
    }

    // User is active - refresh token
    console.log(`[InactivityService] User active (idle for ${Math.round(idleTime / 1000)}s) - refreshing token`);
    await this.refreshAccessToken();
  }

  /**
   * Refresh access token using refresh_token
   */
  private async refreshAccessToken(): Promise<boolean> {
    // Prevent concurrent refresh requests
    if (this.isRefreshing) {
      console.log('[InactivityService] Token refresh already in progress');
      return false;
    }

    const refreshToken = this.getStoredRefreshToken();

    if (!refreshToken) {
      console.error('[InactivityService] No refresh token found');
      console.error('[InactivityService] ⚠️ Skipping token refresh - will retry next interval');
      return false;
    }

    this.isRefreshing = true;

    try {
      const fullUrl = `${PUBLIC_API_BASE_URL}${TOKEN_REFRESH_ENDPOINT}`;
      console.log('[InactivityService] Refreshing token via:', fullUrl);

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      console.log('[InactivityService] Token refresh response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[InactivityService] Token refresh failed:', response.status, errorText);
        this.isRefreshing = false;

        // Check for account locked error (403 with ACCOUNT_LOCKED code)
        if (response.status === 403) {
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.code === 'ACCOUNT_LOCKED') {
              console.error('[InactivityService] Account has been locked - redirecting to login');

              // Clear tokens (cookies + localStorage)
              clearTokens();
              sessionStorage.clear();

              // Stop service
              this.cleanup();

              // Redirect to login with account locked error
              window.location.href = getLoginUrl('error=account_locked');
              return false;
            }
          } catch (e) {
            // If parsing fails, continue with normal error handling
          }
        }

        // Only logout on 401 Unauthorized (refresh token expired/invalid)
        if (response.status === 401) {
          console.error('[InactivityService] Refresh token expired - logging out');
          this.logout();
        } else {
          console.error('[InactivityService] ⚠️ Temporary error - will retry next interval');
        }
        return false;
      }

      const data: RefreshTokenResponse = await response.json();
      console.log('[InactivityService] Token refresh response:', {
        success: data.success,
        hasAccessToken: !!data.access_token,
        hasRefreshToken: !!data.refresh_token
      });

      if (!data.success || !data.access_token) {
        console.error('[InactivityService] Invalid token refresh response');
        this.isRefreshing = false;
        return false;
      }

      // Update stored tokens
      this.storeTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
      });

      console.log('[InactivityService] ✅ Token refreshed successfully');

      // Dispatch custom event to notify other parts of the app
      window.dispatchEvent(
        new CustomEvent('token-refreshed', {
          detail: { accessToken: data.access_token },
        })
      );

      this.isRefreshing = false;
      return true;
    } catch (error) {
      console.error('[InactivityService] ❌ Token refresh exception:', error);
      this.isRefreshing = false;
      // Don't logout on network errors - just log and retry next interval
      console.error('[InactivityService] ⚠️ Network error - will retry next interval');
      return false;
    }
  }

  /**
   * Get refresh token from localStorage
   */
  private getStoredRefreshToken(): string | null {
    return getStoredRefresh();
  }

  /**
   * Store tokens in localStorage
   */
  private storeTokens(tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }): void {
    setStoredAccessToken(tokens.accessToken);
    setStoredRefreshToken(tokens.refreshToken);

    // Also store in memory for immediate access by other services
    (window as any).__authToken = tokens.accessToken;
    (window as any).__refreshToken = tokens.refreshToken;
  }

  /**
   * Logout user - clear tokens and redirect to login
   */
  private logout(): void {
    console.log('[InactivityService] Logging out user due to inactivity');

    // Stop all intervals
    this.cleanup();

    // Use the comprehensive logout service
    performLogout(getLoginUrl()).catch((error) => {
      console.error('[InactivityService] Error during logout:', error);
      // Fallback: redirect to login anyway
      window.location.href = getLoginUrl();
    });
  }

  /**
   * Notify service that video is playing
   * Starts heartbeat to keep session alive
   */
  public onVideoPlay(): void {
    console.log('[InactivityService] Video playback started - enabling heartbeat');
    this.isVideoPlaying = true;

    // Immediately refresh token
    this.refreshAccessToken();

    // Set up heartbeat to refresh every 4 minutes while playing
    if (!this.videoHeartbeatIntervalId) {
      this.videoHeartbeatIntervalId = setInterval(() => {
        console.log('[InactivityService] Video heartbeat - refreshing token');
        this.refreshAccessToken();
      }, REFRESH_INTERVAL);
    }
  }

  /**
   * Notify service that video has paused or ended
   * Stops heartbeat and resumes normal activity tracking
   */
  public onVideoPauseOrEnd(): void {
    console.log('[InactivityService] Video playback paused/ended - resuming activity tracking');
    this.isVideoPlaying = false;

    // Clear heartbeat interval
    if (this.videoHeartbeatIntervalId) {
      clearInterval(this.videoHeartbeatIntervalId);
      this.videoHeartbeatIntervalId = null;
    }

    // Update last activity time to prevent immediate logout
    this.updateLastActivity();
  }

  /**
   * Cleanup service - remove listeners and intervals
   */
  public cleanup(): void {
    console.log('[InactivityService] Cleaning up');

    // Remove activity listeners
    this.activityListeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler);
    });
    this.activityListeners = [];

    // Clear intervals
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }

    if (this.videoHeartbeatIntervalId) {
      clearInterval(this.videoHeartbeatIntervalId);
      this.videoHeartbeatIntervalId = null;
    }
  }

  /**
   * Check if service is initialized
   */
  public isInitialized(): boolean {
    return this.refreshIntervalId !== null;
  }

  /**
   * Get current idle time in milliseconds
   */
  public getIdleTime(): number {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Get time until logout in milliseconds
   */
  public getTimeUntilLogout(): number {
    const idleTime = this.getIdleTime();
    const timeRemaining = INACTIVITY_TIMEOUT - idleTime;
    return Math.max(0, timeRemaining);
  }

  /**
   * Force a token refresh (useful for testing)
   */
  public async forceRefresh(): Promise<boolean> {
    return this.refreshAccessToken();
  }
}

// Export singleton instance
export const inactivityService = new InactivityService();

// Expose on window for cross-package access (LMS, etc.)
if (typeof window !== 'undefined') {
  (window as any).__inactivityService = inactivityService;
}

// Export for testing and type definitions
export default InactivityService;
export type { RefreshTokenResponse };
